/**
 * @praesto/core/auth — Firebase Auth multi-tenant wrapper.
 *
 * The signed-in user is exposed synchronously (`currentUser()` per contract),
 * so the Firestore profile doc is loaded reactively on every id-token change
 * and cached. `tenantId` comes from the verified JWT claim / RuntimeContext —
 * never a caller argument (fail-closed multi-tenancy, root CLAUDE.md).
 *
 * Contract: packages/contracts/src/core-api.ts (AuthService).
 */
import type { AuthService, Unsubscribe } from "@praesto/contracts";
import type { User } from "@praesto/shared-types";
import { type User as FbUser, onIdTokenChanged, signOut as fbSignOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import type { RuntimeContext } from "../internal/context";
import { tenantPath } from "../internal/paths";

type Listener = (u: User | null) => void;

export function createAuthService(ctx: RuntimeContext): AuthService {
  const { auth, db } = ctx.firebase;
  let cache: User | null = null;
  // Tracks whether Firebase Auth has finished rehydrating the cached
  // IndexedDB session (or confirmed there is none). Until then, any
  // synchronous `cb(cache)` emission would falsely report `null` and
  // bounce the user to the login screen on every page reload.
  let initialResolved = false;
  const listeners = new Set<Listener>();

  const notify = (): void => {
    for (const l of Array.from(listeners)) l(cache);
  };

  async function loadUser(fb: FbUser): Promise<User> {
    const snap = await getDoc(doc(db, tenantPath(ctx.tenantId, "users", fb.uid)));
    const data = (snap.data() ?? {}) as Partial<User>;
    return {
      id: fb.uid,
      tenantId: ctx.tenantId,
      email: data.email ?? fb.email ?? "",
      displayName: data.displayName ?? fb.email ?? fb.uid,
      locale: data.locale ?? "it",
      mfaEnabled: data.mfaEnabled ?? false,
      notificationPreferences: data.notificationPreferences ?? {
        channels: { inApp: true, sms: false, whatsapp: false, push: false, email: false },
        perType: {},
      },
      status: data.status ?? "active",
      ...(data.phone ? { phone: data.phone } : {}),
    };
  }

  onIdTokenChanged(auth, (fb) => {
    if (!fb) {
      cache = null;
      initialResolved = true;
      notify();
      return;
    }
    void loadUser(fb)
      .then((u) => {
        cache = u;
      })
      .catch((err: unknown) => {
        // The session's token still refreshes, but its tenant user-doc read was
        // denied (e.g. a stale/foreign cached session whose claims don't match
        // this tenant). Fail closed: treat it as signed-out so the boot gate
        // resolves to the login screen instead of wedging forever on
        // "Avvio in corso…". A transient error self-heals on the next token
        // refresh (loadUser retries and can populate `cache`).
        cache = null;
        console.warn("[auth] loadUser failed; treating session as signed out", err);
      })
      .finally(() => {
        initialResolved = true;
        notify();
      });
  });

  return {
    currentUser: () => cache,
    currentUserId: () => cache?.id ?? null,

    onAuthChange(cb: Listener): Unsubscribe {
      listeners.add(cb);
      // Only emit synchronously once Firebase has confirmed the initial
      // auth state. Emitting `null` before rehydration completes makes
      // the boot consumer flip to `phase: "login"` before the cached
      // session has had a chance to restore.
      if (initialResolved) cb(cache);
      return () => {
        listeners.delete(cb);
      };
    },

    async getIdToken(forceRefresh?: boolean): Promise<string> {
      const fb = auth.currentUser;
      if (!fb) throw new Error("[auth] getIdToken: no authenticated user");
      return fb.getIdToken(forceRefresh ?? false);
    },

    async signOut(): Promise<void> {
      await fbSignOut(auth);
    },
  };
}
